<template>
  <div class="analytics-chart">
    <div
        v-for="(item, i) in rows"
        :key="i"
        class="analytics-chart__row"
        :class="[
          { 'analytics-chart__row--selected': item.type === 'PRP' },
          statRowClass(item.type),
          { 'analytics-chart__row--accent-soft': isSoftAccentType(item.type) },
        ]"
    >
      <div class="left">
        <span class="name">{{ item.name }}</span>
        <v-icon
          v-if="item.type === 'PRP'"
          small
          class="system-predicted-icon"
          title="System predicted"
          aria-label="System predicted"
        >
          mdi-star-circle
        </v-icon>
        <span v-else-if="shouldShowTag(item)" class="tag" :class="item.type">{{ tag(item.type) }}</span>
      </div>

      <div class="right">
        <span class="value">{{ smart(item.value) }}</span>
        <span
            class="delta"
            :class="{
            pos: item.delta > 0,
            neg: item.delta < 0
          }"
        >
          {{ formatDelta(item.delta) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { smartPruneNumber } from "@/utils/number";

/** Rows with dedicated Min/Avg/Max/Selected styling — anything else (e.g. STANDARD) uses soft accent like Selected */
const FIXED_STYLE_TYPES = new Set(["PRP", "MIN", "MAX", "AVG"]);

export default {
  name: "AnalyticsChart",
  props: {
    internalSeriesData: Array
  },
  computed: {
    avg() {
      return this.internalSeriesData.find(i => i.type === "AVG")?.value || 0;
    },
    rows() {
      const priority = { PRP: 0, AVG: 1, MIN: 2, MAX: 3 };

      return this.internalSeriesData
          .map(i => ({
            ...i,
            delta: i.value - this.avg
          }))
          .sort((a, b) => (priority[a.type] ?? 10) - (priority[b.type] ?? 10))
          .reverse();
    }
  },
  methods: {
    statRowClass(type) {
      if (type === "MIN") return "analytics-chart__row--min";
      if (type === "AVG") return "analytics-chart__row--avg";
      if (type === "MAX") return "analytics-chart__row--max";
      return "";
    },
    isSoftAccentType(type) {
      return Boolean(type) && !FIXED_STYLE_TYPES.has(type);
    },
    smart(v) {
      return smartPruneNumber(v);
    },
    formatDelta(v) {
      if (!v) return "0";
      return (v > 0 ? "+" : "") + smartPruneNumber(v);
    },
    tag(type) {
      if (type === "STANDARD" || type === "PRP") return "";
      const known = {
        AVG: "Avg",
        MIN: "Min",
        MAX: "Max",
      }[type];
      if (known) return known;
      return type || "—";
    },
    shouldShowTag(item) {
      if (item.type === "PRP") return false;
      const label = this.tag(item.type);
      if (!label) return false;
      return label.toLowerCase() !== String(item.name).trim().toLowerCase();
    },
  }
};
</script>

<style scoped>
/*
  Structure only — theme colors live in the unscoped block so we can target
  html.dark + .v-application.theme--dark / .theme--light (same as CastingExploreResult.vue).
*/
.analytics-chart {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.analytics-chart__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  border-left: 2px solid transparent;
}

.left {
  display: flex;
  gap: 6px;
  align-items: center;
}

.right {
  display: flex;
  gap: 10px;
  align-items: center;
}

.system-predicted-icon {
  flex-shrink: 0;
  opacity: 0.9;
}
</style>

<style lang="scss">
/*
  Light + dark (match CastingExploreResult / InvoicesTable):
  - Light: .v-application.theme--light
  - Dark: html.dark (Tailwind, App.vue) AND .v-application.theme--dark (Vuetify)
*/

/* —— Light —— (flat fills + left accent; no gradients) */
.v-application.theme--light .analytics-chart .analytics-chart__row {
  border-top: none;
  border-right: none;
  border-left: 2px solid transparent;
  border-bottom: none;
  background: var(--color-gray-50);
}

.v-application.theme--light .analytics-chart .analytics-chart__row--selected {
  border-left-color: #14b8a6;
  background: #ecfeff;
}

/* User-added rows (e.g. STANDARD): same family as Selected, lighter */
.v-application.theme--light .analytics-chart .analytics-chart__row--accent-soft {
  border-left-color: #5eead4;
  background: #f0fdfa;
}

.v-application.theme--light .analytics-chart .analytics-chart__row--accent-soft .tag {
  color: #0d9488;
}

.v-application.theme--light .analytics-chart .analytics-chart__row--min {
  border-left-color: #f87171;
  background: #fef2f2;
}

.v-application.theme--light .analytics-chart .analytics-chart__row--avg {
  border-left-color: #eab308;
  background: #fffbeb;
}

.v-application.theme--light .analytics-chart .analytics-chart__row--max {
  border-left-color: #4ade80;
  background: #f0fdf4;
}

.v-application.theme--light .analytics-chart .name {
  font-weight: 600;
  color: var(--color-gray-800);
}

.v-application.theme--light .analytics-chart .tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0;
  border-radius: 0;
  border: none;
  background: transparent;
  color: var(--color-gray-500);
}

.v-application.theme--light .analytics-chart .tag.PRP {
  color: #0d9488;
}

.v-application.theme--light .analytics-chart .system-predicted-icon {
  color: #0d9488 !important;
}

.v-application.theme--light .analytics-chart .tag.AVG {
  color: #a16207;
}

.v-application.theme--light .analytics-chart .tag.MIN {
  color: #b91c1c;
}

.v-application.theme--light .analytics-chart .tag.MAX {
  color: #15803d;
}

.v-application.theme--light .analytics-chart .value {
  font-weight: 600;
  color: var(--color-gray-900);
}

.v-application.theme--light .analytics-chart .delta {
  font-size: 11px;
  font-weight: 600;
}

.v-application.theme--light .analytics-chart .delta.pos {
  color: #15803d;
}

.v-application.theme--light .analytics-chart .delta.neg {
  color: #b91c1c;
}

/* —— Dark —— (flat fills; color-mix = single blended tone, not a gradient) */
html.dark .analytics-chart .analytics-chart__row,
.v-application.theme--dark .analytics-chart .analytics-chart__row {
  border-top: none;
  border-right: none;
  border-left: 2px solid transparent;
  border-bottom: none;
  background: var(--color-gray-800);
}

html.dark .analytics-chart .analytics-chart__row--selected,
.v-application.theme--dark .analytics-chart .analytics-chart__row--selected {
  border-left-color: #2dd4bf;
  background: color-mix(in srgb, #0f766e 22%, var(--color-gray-800));
}

html.dark .analytics-chart .analytics-chart__row--accent-soft,
.v-application.theme--dark .analytics-chart .analytics-chart__row--accent-soft {
  border-left-color: #5eead4;
  background: color-mix(in srgb, #0f766e 11%, var(--color-gray-800));
}

html.dark .analytics-chart .analytics-chart__row--accent-soft .tag,
.v-application.theme--dark .analytics-chart .analytics-chart__row--accent-soft .tag {
  color: #5eead4;
}

html.dark .analytics-chart .analytics-chart__row--min,
.v-application.theme--dark .analytics-chart .analytics-chart__row--min {
  border-left-color: #f87171;
  background: color-mix(in srgb, #7f1d1d 28%, var(--color-gray-800));
}

html.dark .analytics-chart .analytics-chart__row--avg,
.v-application.theme--dark .analytics-chart .analytics-chart__row--avg {
  border-left-color: #eab308;
  background: color-mix(in srgb, #713f12 30%, var(--color-gray-800));
}

html.dark .analytics-chart .analytics-chart__row--max,
.v-application.theme--dark .analytics-chart .analytics-chart__row--max {
  border-left-color: #4ade80;
  background: color-mix(in srgb, #14532d 30%, var(--color-gray-800));
}

html.dark .analytics-chart .name,
.v-application.theme--dark .analytics-chart .name {
  font-weight: 600;
  color: var(--color-gray-200);
}

html.dark .analytics-chart .tag,
.v-application.theme--dark .analytics-chart .tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0;
  border-radius: 0;
  border: none;
  background: transparent;
  color: var(--color-gray-400);
}

html.dark .analytics-chart .tag.PRP,
.v-application.theme--dark .analytics-chart .tag.PRP {
  color: #5eead4;
}

html.dark .analytics-chart .system-predicted-icon,
.v-application.theme--dark .analytics-chart .system-predicted-icon {
  color: #5eead4 !important;
}

html.dark .analytics-chart .tag.AVG,
.v-application.theme--dark .analytics-chart .tag.AVG {
  color: #facc15;
}

html.dark .analytics-chart .tag.MIN,
.v-application.theme--dark .analytics-chart .tag.MIN {
  color: #fca5a5;
}

html.dark .analytics-chart .tag.MAX,
.v-application.theme--dark .analytics-chart .tag.MAX {
  color: #86efac;
}

html.dark .analytics-chart .value,
.v-application.theme--dark .analytics-chart .value {
  font-weight: 600;
  color: var(--color-gray-100);
}

html.dark .analytics-chart .delta,
.v-application.theme--dark .analytics-chart .delta {
  font-size: 11px;
  font-weight: 600;
}

html.dark .analytics-chart .delta.pos,
.v-application.theme--dark .analytics-chart .delta.pos {
  color: #86efac;
}

html.dark .analytics-chart .delta.neg,
.v-application.theme--dark .analytics-chart .delta.neg {
  color: #fca5a5;
}
</style>